import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * AUT-419: coordinates a sticky bulk-actions bar with a sticky table header. The bar registers its
 * own element; the shared `Table` reads the measured height and pins its header just below the bar,
 * so both stay visible while the page scrolls (the bar sits on top, column labels directly under it).
 *
 * Any table under a provider auto-consumes the offset — no per-table prop drilling. A table with no
 * provider gets the default (0) and behaves exactly as before.
 */
interface StickyToolbarContextValue {
  height: number;
  register: (el: HTMLElement | null) => void;
}

const StickyToolbarContext = createContext<StickyToolbarContextValue>({
  height: 0,
  register: () => {},
});

export function StickyToolbarProvider({ children }: { children: ReactNode }) {
  const [height, setHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const register = useCallback((el: HTMLElement | null) => {
    observerRef.current?.disconnect();
    if (!el) {
      setHeight(0);
      return;
    }
    const measure = () => setHeight(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    observerRef.current = ro;
    measure();
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return (
    <StickyToolbarContext.Provider value={{ height, register }}>
      {children}
    </StickyToolbarContext.Provider>
  );
}

/** For the sticky bar: a ref callback that reports the bar's height (and 0 once it unmounts). */
export function useStickyToolbarRegister(): (el: HTMLElement | null) => void {
  return useContext(StickyToolbarContext).register;
}

/** For the table: the current sticky-toolbar height to add to the header's pin offset. */
export function useStickyToolbarHeight(): number {
  return useContext(StickyToolbarContext).height;
}
